import React, {useMemo} from 'react';
import cn from 'classnames';
import styles from '../styles.module.scss';
import useTranslation from 'next-translate/useTranslation';
import PropTypes from 'prop-types';
import Ok from '../../Icons/Common/Ok';
import Cancel from '../../Icons/Common/Cancel';
import {COMMON_NAMESPACE} from '../../../../../utils/i18nNameSpaces';

export const PRICING_HASH_TAG = 'pricing';
const tableDataShortedCount = 28;

const PricingTable = (
  {
    namespace,
    plan,
    pricing,
  }) => {
  const {t} = useTranslation(namespace);
  const free = plan?.free || null;
  const payAsGo = plan?.payAsGo || null;
  const starter = plan?.starter || null;
  const standard = plan?.standard || null;
  const advance = plan?.advance || null;
  const enterPrice = plan?.enterPrice || null;

  const okIcon = useMemo(() => {
    return (
      <div className={styles['check-mark']}>
        <Ok/>
      </div>
    );
  }, []);

  const noOkIcon = useMemo(() => {
    return (
      <div className={styles['check-mark']}>
        <Cancel/>
      </div>
    );
  }, []);

  const showTrueFalse = useMemo(() => (plan, i) => {

    return (
      (plan.data[i] === true || plan.data[i] === 'true')
        ? okIcon
        : plan.data[i] === false || plan.data[i] === 'false'
          ? noOkIcon
          : plan.data[i]

    );
  }, []);

  return (
    <div
      className={cn(styles['pricing__table-wrapper'], 'pricing__table-wrapper')}
      id={PRICING_HASH_TAG}>
      <table>
        <thead>
        <tr>
          <th>&nbsp;</th>
          {free &&
            <th>
              {free.header.name}
              <div className={styles['table__header-wrapper']}>
                <span className={styles['dollar']}>$</span>
                <span
                  className={styles['table__header-price']}>{free.price}</span>
                <span
                  className={styles['table__header-label']}>&nbsp;USD/<span>{pricing.payment.monthly.free.label}</span></span>
              </div>
            </th>
          }
          {payAsGo &&
            <th>
              {payAsGo.header.name}
              <div className={styles['table__header-wrapper']}>
                <span className={styles['dollar']}>$</span>
                <span
                  className={styles['table__header-price']}>{payAsGo.price}</span>
                <span
                  className={styles['table__header-label']}>&nbsp;USD/<span>{pricing.payment.monthly.payAsGo.label}</span></span>
              </div>
            </th>
          }
          {starter &&
            <th>
              {starter.header.name}
              <div className={styles['table__header-wrapper']}>
                <span className={styles['dollar']}>$</span>
                <span
                  className={styles['table__header-price']}>{starter.price}</span>
                <span
                  className={styles['table__header-label']}>&nbsp;USD/<span>{pricing.payment.monthly.starter.label}</span></span>
              </div>
            </th>
          }
          {standard &&
            <th>
              {standard.header.name}
              {!standard.noFreeDays && !standard.mostPopular &&
                <div className={styles['trial-label']}>{t(
                  'pricing.payment.trial')}</div>}
              {standard.mostPopular && <div
                className={cn(styles['trial-label'],
                  styles['most-popular'])}>{t(
                `${COMMON_NAMESPACE}:mostPopular`)}</div>}
              <div className={styles['table__header-wrapper']}>
                <span className={styles['dollar']}>$</span>
                <span
                  className={styles['table__header-price']}>{standard.price}</span>
                <span
                  className={styles['table__header-label']}>&nbsp;USD/<span>{plan.label ||
                  pricing.payment.monthly.standard.label}</span></span>
              </div>
              {standard.discount &&
                <div className={styles['default-price__wrapper']}>
                  $&nbsp;
                  {standard.discount.value}
                  <span>
                  &nbsp; USD
                  <span>
                    /{pricing.payment.monthly.advance.label}
                  </span>
                </span>
                </div>
              }
            </th>
          }
          {advance &&
            <th>
              {advance.header.name}
              {!advance.noFreeDays && <div className={styles['trial-label']}>{t(
                'pricing.payment.trial')}</div>}
              {advance.mostPopular && <div
                className={cn(styles['trial-label'],
                  styles['most-popular'])}>{t(
                `${COMMON_NAMESPACE}:mostPopular`)}</div>}
              <div className={styles['table__header-wrapper']}>
                <span className={styles['dollar']}>$</span>
                <span
                  className={styles['table__header-price']}>{advance.price}</span>
                <span
                  className={styles['table__header-label']}>&nbsp;USD/<span>{pricing.payment.monthly.advance.label}</span></span>
              </div>
              {advance.discount &&
                <div className={styles['default-price__wrapper']}>
                  $&nbsp;
                  {advance.discount.value}
                  <span>
                  &nbsp;USD
                  <span>
                    /{pricing.payment.monthly.advance.label}
                  </span>
                </span>
                </div>
              }
            </th>
          }
          {enterPrice &&
            <th className={'trial-label'}>
              {enterPrice.header.name}
              {!enterPrice.noFreeDays &&
                <div className={styles['trial-label']}>{t(
                  'pricing.payment.trial')}</div>}
              {enterPrice.mostPopular && <div
                className={cn(styles['trial-label'],
                  styles['most-popular'])}>{t(
                `${COMMON_NAMESPACE}:mostPopular`)}</div>}
              <div className={styles['table__header-wrapper']}>
                <span className={styles['dollar']}>$</span>
                <span
                  className={styles['table__header-price']}>{enterPrice.price}</span>
                <span
                  className={styles['table__header-label']}>&nbsp;USD/<span>{pricing.payment.monthly.advance.label}</span></span>
              </div>
              {enterPrice.discount &&
                <div className={styles['default-price__wrapper']}>
                  $&nbsp;
                  {enterPrice.discount.value}
                  <span>
                  &nbsp;USD
                  <span>
                    /{pricing.payment.monthly.advance.label}
                  </span>
                </span>
                </div>
              }
            </th>
          }
        </tr>
        </thead>
        <tbody>
        <tr className={styles['layer']}/>
        <tr style={{borderCollapse: 'separate', margin: 2}}>
          <td colSpan="1" className={styles['table__feature']}>
            {t('pricing.features.name')}
          </td>
          <td colSpan={Object.keys(plan).length - 1} style={{background: '#EDEEEF'}}>
          </td>
        </tr>
        <tr className={styles['layer']}/>
        {standard.data.map((el, i) => (
          <tr key={i}>
            <td className={styles['table__feature-name']}>
              <div className={styles['table__feature-container']}>
                <span>
                {t(`pricing.features._${i + 1}.name`)}
                </span>
                <span className={styles['table__feature-subdued']}>
                  {t(`pricing.features._${i + 1}.description`)}
                </span>
              </div>
            </td>
            {free &&
              <td
                className={
                  cn(
                    styles['table__data'],
                    {
                      [styles['table__data--shorted']]: free?.data[i].length >
                      tableDataShortedCount,
                    },
                  )
                }
                width={250}>
                {showTrueFalse(free, i)}
              </td>
            }
            {payAsGo &&
              <td
                className={
                  cn(
                    styles['table__data'],
                    {
                      [styles['table__data--shorted']]: payAsGo?.data[i].length >
                      tableDataShortedCount,
                    },
                  )
                }
                width={250}>
                {showTrueFalse(payAsGo, i)}
              </td>
            }
            {starter &&
              <td
                className={
                  cn(
                    styles['table__data'],
                    {
                      [styles['table__data--shorted']]: starter?.data[i].length >
                      tableDataShortedCount,
                    },
                  )
                }
                width={250}>
                {showTrueFalse(starter, i)}
              </td>
            }
            {standard &&
              <td
                className={cn(styles['table__data'], {
                  [styles['table__data--shorted']]: standard?.data[i].length >
                  tableDataShortedCount,
                })}>
                {showTrueFalse(standard, i)}
              </td>
            }
            {advance &&
              <td
                className={cn(styles['table__data'], {
                  [styles['table__data--shorted']]: advance?.data[i].length >
                  tableDataShortedCount,
                })}>
                {showTrueFalse(advance, i)}
              </td>
            }
            {enterPrice &&
              <td
                className={cn(styles['table__data'], {
                  [styles['table__data--shorted']]: enterPrice?.data[i].length >
                  tableDataShortedCount,
                })}>
                {showTrueFalse(enterPrice, i)}
              </td>
            }
          </tr>
        ))}
        </tbody>
        {/*<tfoot>*/}
        {/*<tr>*/}
        {/*  <td/>*/}
        {/*  {free && <td><Button url={link} external fullWidth primary disabled={linkDisabled}>{free.link.text}</Button></td>}*/}
        {/*  {standard && <td><Button url={link} external fullWidth primary disabled={linkDisabled}>{standard.link.text}</Button></td>}*/}
        {/*  {advance && <td><Button url={link} external fullWidth primary disabled={linkDisabled}>{advance.link.text}</Button></td>}*/}
        {/*  {enterPrice && <td><Button url={link} external fullWidth primary disabled={linkDisabled}>{enterPrice.link.text}</Button></td>}*/}
        {/*</tr>*/}
        {/*</tfoot>*/}
      </table>
    </div>
  );
};

PricingTable.propTypes = {
  namespace: PropTypes.oneOf(['selectors', 'reactFlow', 'recharge']),
};

export default PricingTable;
